
import stores from "@/stores";
import { __SignIn } from "@/stores/auth/action-dto";
import { defineComponent } from "vue";
import ISignInForm from "@/types/forms/sign-in-form.type";

/* components */
import CInputLogin from "@/components/shared/input/CInputLogin.vue";
import CCheckBox from "@/components/shared/checkbox/CheckBox.vue";
import ModalOBPassword from "@/components/auth/ModalOBPassword.vue";
import ModalBlockLogin from "@/components/auth/ModalBlockLogin.vue";
import RecaptchaV2Widget from "@/components/auth/RecaptchaV2Widget.vue";

/* library toast */
import "vue3-toastify/dist/index.css";

/* Service */
import ToastService from "@/services/Toast.service";
import ValidatorsService from "@/services/Validators.service";
import shareHelper from "@/components/shared/helper/shareHelper";
import { executeV3, loadRecaptchaScript } from "@/assets/utils/recaptchaHelper";

const ATTEMPTS_SHOW_OB_PASSWORD = 3;
const NO_REMAINING_ATTEMPTS = 0;
const MIN_ATTEMPTS_SHOW_COUNT = 1;
const MINUTES_PER_HOUR = 60;
const MILLISECONDS_PER_MINUTE = 60000;
const MIN_LOCK_MINUTES = 1;

const RECAPTCHA_V3_SITE_KEY =
  process.env.VUE_APP_RecaptchaEnterprise_ScoreSiteKey || process.env.VUE_APP_RecaptchaV3_SiteKey || "";

export default defineComponent({
  /* name */
  name: "SignInForm",

  /* components */
  components: {
    CInputLogin,
    CCheckBox,
    ModalOBPassword,
    ModalBlockLogin,
    RecaptchaV2Widget,
  },

  /* props */
  props: {
    getSignInError: {
      type: Number,
      require: true,
    },
  },

  /* data */
  data() {
    return {
      signInForm: {
        username: "",
        password: "",
        keep_me_logged_in: true,
      } as ISignInForm,
      status: {
        loginRound: 0,
        valid: false,
      },
      inputValue: "",
      showModalOBPassword: false,
      showModalBlockLogin: false,
      blockLoginLockDurationMinutes: MIN_LOCK_MINUTES,
      recaptchaV2Token: "",
      recaptchaBlocked: false,
    };
  },

  /* computed */
  computed: {
    loginGuardData() {
      return stores.getters["auth/loginGuardData"];
    },
    loginLockedData() {
      return stores.getters["auth/loginLockedData"];
    },
    needRecaptchaV2(): boolean {
      return stores.getters["auth/needRecaptchaV2"];
    },
    signInLoading(): boolean {
      return stores.getters["auth/signInLoading"];
    },
    isLoginDisabled(): boolean {
      if (this.signInLoading) return true;
      if (this.recaptchaBlocked) return true;
      if (this.needRecaptchaV2 && !this.recaptchaV2Token) return true;
      return false;
    },
  },

  /* lifecycle */
  mounted() {
    document.body.classList.add("recaptcha-visible");
    if (RECAPTCHA_V3_SITE_KEY) {
      // UI is English-only; pin reCAPTCHA badge language to match
      loadRecaptchaScript(RECAPTCHA_V3_SITE_KEY, "en").catch(() => {
        // silent — script load failure should not block login
      });
    }
  },
  beforeUnmount() {
    document.body.classList.remove("recaptcha-visible");
  },

  /* methods */
  methods: {
    async onLogin(recaptchaOverride?: { token: string; version: string }) {
      if (!recaptchaOverride) {
        const { username, password } = this.signInForm;
        const isUsernameEmpty = shareHelper.checkMaxLength(username, 0);
        const isPasswordEmpty = shareHelper.checkMaxLength(password, 0);

        if (isUsernameEmpty && isPasswordEmpty) {
          this.status = { ...this.status, valid: true };
          return ToastService.showWarning("Please input your email and password");
        } else if (isUsernameEmpty) {
          return ToastService.showWarning("Please input your email");
        } else if (isPasswordEmpty) {
          return ToastService.showWarning("Please input your password");
        }
      }

      /* Sign In */
      const payload: __SignIn = { ...this.signInForm };

      if (recaptchaOverride) {
        payload.recaptchaToken = recaptchaOverride.token;
        payload.recaptchaVersion = recaptchaOverride.version;
      } else {
        let v3Token = "";
        try {
          v3Token = await executeV3(RECAPTCHA_V3_SITE_KEY, "login");
        } catch {
          // script failed to load/execute — proceed without token
        }
        if (v3Token) {
          payload.recaptchaToken = v3Token;
          payload.recaptchaVersion = "v3";
        }
      }

      stores.dispatch("auth/signIn", payload);
    },
    onRecaptchaV2Verify(token: string) {
      this.recaptchaV2Token = token;
      this.recaptchaBlocked = false;
      this.onLogin({ token, version: "v2" });
    },
    onRecaptchaV2Expired() {
      this.recaptchaV2Token = "";
    },
    onRecaptchaV2Error() {
      this.recaptchaV2Token = "";
      this.recaptchaBlocked = true;
    },
    checkKeyboard(el, type) {
      if (type === "text" || type === "password") {
        if (type === "text") {
          this.signInForm.username = el;
        } else if (type === "password") {
          this.signInForm.password = el;
        }
      }
      /* check keyboard 5 character */
      const checkKeyboard = shareHelper.checkKeyboard(el);
      if (!checkKeyboard) return;
      ToastService.showWarning("Your keyboard is not set to English");
    },
    checkEmail(el) {
      const value = el.target.value;
      if (value && ValidatorsService.validateEmail(value)) {
        if (shareHelper.checkMaxLengthEmail(this.signInForm.username)) {
          return;
        }
        return;
      } else {
        ToastService.showWarning(
          "Incorrect email format. Please recheck again."
        );
      }
    },
    onForgotPassword() {
      const baseUrl = process.env.VUE_APP_BASE_URL_PEAK;
      const username = this.signInForm.username;
      window.open(`${baseUrl}/forgotPwd?a=${username}`, "_blank");
    },
    ParseLockedUntilAsUtc(lockedUntilStr: string): number {
      if (!lockedUntilStr) return MIN_LOCK_MINUTES;

      const now = new Date();
      const normalized = lockedUntilStr.trim().replace(" ", "T");

      const asUtc = new Date(normalized + "Z");
      const remainingUtcMinutes = Math.ceil(
        (asUtc.getTime() - now.getTime()) / MILLISECONDS_PER_MINUTE
      );

      const asLocal = new Date(normalized);
      const remainingLocalMinutes = Math.ceil(
        (asLocal.getTime() - now.getTime()) / MILLISECONDS_PER_MINUTE
      );

      const remainingMinutes = Math.max(remainingUtcMinutes, remainingLocalMinutes);
      return Math.max(remainingMinutes, MIN_LOCK_MINUTES);
    },
    FormatLockDuration(totalMinutes: number): string {
      const safeTotalMinutes = Math.max(totalMinutes, MIN_LOCK_MINUTES);
      if (safeTotalMinutes >= MINUTES_PER_HOUR) {
        const hours = Math.floor(safeTotalMinutes / MINUTES_PER_HOUR);
        const minutes = safeTotalMinutes % MINUTES_PER_HOUR;
        if (minutes === 0) {
          return `${hours} ${hours === 1 ? "hour" : "hours"}`;
        }
        return `${hours} ${hours === 1 ? "hour" : "hours"} ${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
      }
      return `${safeTotalMinutes} ${safeTotalMinutes === 1 ? "minute" : "minutes"}`;
    },
    HandleLoginLocked(lockedData: any) {
      const lockMinutes = this.ParseLockedUntilAsUtc(lockedData.lockedUntil);
      const duration = this.FormatLockDuration(lockMinutes);
      ToastService.showError(`Please try again in ${duration}`);
    },
    HandleLoginGuardFailAttempt(guardData: any) {
      const failedAttempts = Number(guardData.failed_attempts) || MIN_ATTEMPTS_SHOW_COUNT;
      const remainingAttempts = Number(guardData.remaining_attempts) || 0;
      const totalAttempts = failedAttempts + remainingAttempts;

      if (remainingAttempts === NO_REMAINING_ATTEMPTS) {
        const lockMinutes = Number(guardData.next_lock_duration_minutes) || MIN_LOCK_MINUTES;
        const duration = this.FormatLockDuration(lockMinutes);
        ToastService.showError(`Please try again in ${duration}`);
        this.blockLoginLockDurationMinutes = lockMinutes;
        this.showModalBlockLogin = true;
        return;
      }

      ToastService.showError(
        `Email or password is incorrect. Please try again (${failedAttempts}/${totalAttempts})`
      );
      if (failedAttempts === ATTEMPTS_SHOW_OB_PASSWORD) {
        this.showModalOBPassword = true;
      }
    },
  },

  /* watch */
  watch: {
    needRecaptchaV2(newValue: boolean) {
      // single-use token: clear on a new challenge so the button re-disables
      if (newValue) {
        this.recaptchaV2Token = "";
      }
    },
    getSignInError(newValue) {
      if (newValue !== null) {
        this.status.loginRound++;
        ToastService.showError(newValue);
      }
    },
    loginLockedData(newValue) {
      if (newValue) {
        this.HandleLoginLocked(newValue);
      }
    },
    loginGuardData(newValue) {
      if (newValue) {
        this.HandleLoginGuardFailAttempt(newValue);
      }
    },
  },
});
